import { useState, useEffect } from "react";
import { UseFormReturn, useController } from "react-hook-form";

import { Field } from "../../types";

import { AutoComplete as AutoCompleteInput } from "@/components/AutoCompleteInput";

interface AutoCompleteProps {
  size?: "desktop" | "dialog";
  field: Field;
  form: UseFormReturn;
  stepId: string;
}

export const AutoComplete = ({ size = "desktop", field, form, stepId }: AutoCompleteProps) => {
  const { trigger, watch } = form;
  const [isInitiated, setIsInitiated] = useState(false);
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const key = `${stepId}.${field.id}`;

  const {
    field: { onChange, value, onBlur },
    fieldState: { error, isDirty },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: val => (isDisplayed ? field.validation?.(val, fieldsInputs) : undefined),
    },
    control: form.control,
    name: key,
  });

  const items =
    typeof field.fieldOptions === "function"
      ? field.fieldOptions(fieldsInputs)
      : field.fieldOptions || [];

  const options = items.map(item => ({
    id: item.id,
    value: item.value,
    label: item.label || item.value,
  }));

  useEffect(() => {
    if (isDisplayed && !isInitiated) {
      setIsInitiated(true);
      trigger(stepId);
    }
  }, [isDisplayed, stepId, trigger, isInitiated]);

  return (
    <div className="relative">
      <AutoCompleteInput
        size={size === "desktop" ? "medium" : "small"}
        label={typeof field.label === "function" ? field.label(fieldsInputs) : field.label}
        readOnly={field.readOnly}
        tooltip={field.tooltip}
        placeholder={
          typeof field.placeholder === "function"
            ? field.placeholder(fieldsInputs)
            : field.placeholder || "Select an option..."
        }
        options={options}
        renderOption={field.renderOption}
        value={value}
        onSelect={onChange}
        onBlur={onBlur}
        isDisplayed={isDisplayed}
        addNewOption={field.addNewOption}
        getAddOptionText={field.getAddOptionText}
      />
      {error && isDirty && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
};
